import React, { useCallback, type ChangeEvent } from 'react';
import classNames from 'classnames';
import { Flex, Icon, Switch, Tooltip } from '@servicetitan/anvil2';
import InfoIcon from '@servicetitan/anvil2/assets/icons/material/round/info.svg';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { audioDevicesState } from '../../audioDevicesState';
import { ringTestService } from '../../utils/audioPlaybackTestService';
import type { AudioDevice } from '../../types';
import { AudioDeviceCombobox } from '../AudioDeviceCombobox/AudioDeviceCombobox';
import { audioDeviceStrings } from '../audioDeviceStrings';
import { AudioTestButton } from './AudioTestButton';
import styles from './RingOutputSection.module.css';

export interface RingOutputSectionProps {
  className?: string;
  isRingOutputEnabled: boolean;
  currentRingDeviceId: string | undefined;
  currentOutputDeviceId: string | undefined;
  currentInputDeviceId: string | undefined;
  audioOutputDevices: readonly AudioDevice[];
  disabled: boolean;
  loading: boolean;
  ringOutputSwitchError: string | null;
  isTestingRingSpeaker: boolean;
  ringSpeakerTestError: string | null;
}

export const RingOutputSection: React.FC<RingOutputSectionProps> = ({
  className,
  isRingOutputEnabled,
  currentRingDeviceId,
  currentOutputDeviceId,
  currentInputDeviceId,
  audioOutputDevices,
  disabled,
  loading,
  ringOutputSwitchError,
  isTestingRingSpeaker,
  ringSpeakerTestError,
}) => {
  const handleToggleChange = useCallback(
    (_e: ChangeEvent<HTMLInputElement>, state: { checked: boolean }) => {
      audioDevicesState.isRingOutputEnabled = state.checked;
      if (
        !state.checked &&
        currentRingDeviceId !== undefined &&
        currentRingDeviceId !== currentOutputDeviceId &&
        currentInputDeviceId !== undefined &&
        currentOutputDeviceId !== undefined
      ) {
        SoftPhoneModule.setAudioDevices(
          currentInputDeviceId,
          currentOutputDeviceId,
          currentOutputDeviceId,
        );
      }
    },
    [currentRingDeviceId, currentOutputDeviceId, currentInputDeviceId],
  );

  const handleRingDeviceChange = useCallback(
    (deviceId: string) => {
      if (
        deviceId === currentRingDeviceId ||
        currentInputDeviceId === undefined ||
        currentOutputDeviceId === undefined
      ) {
        return;
      }
      if (isTestingRingSpeaker) {
        ringTestService.stopTest();
        audioDevicesState.isTestingRingSpeaker = false;
      }
      SoftPhoneModule.setAudioDevices(
        currentInputDeviceId,
        currentOutputDeviceId,
        deviceId,
      );
    },
    [
      currentInputDeviceId,
      currentOutputDeviceId,
      currentRingDeviceId,
      isTestingRingSpeaker,
    ],
  );

  return (
    <div
      className={classNames(styles.section, className)}
      data-testid='ring-output-section'
    >
      <Flex alignItems='center' gap='2' className={styles.toggleRow}>
        <Switch
          checked={isRingOutputEnabled}
          onChange={handleToggleChange}
          disabled={disabled}
          label={audioDeviceStrings.toggle.ringOutputLabel}
          data-testid='ring-output-toggle'
        />
        <Tooltip placement='top'>
          <Tooltip.Trigger>
            <Icon svg={InfoIcon} size='medium' data-testid='ring-output-info' />
          </Tooltip.Trigger>
          <Tooltip.Content>
            {audioDeviceStrings.toggle.ringOutputTooltip}
          </Tooltip.Content>
        </Tooltip>
      </Flex>

      {isRingOutputEnabled && (
        <AudioDeviceCombobox
          data-testid='audio-device-panel-ring-output'
          devices={audioOutputDevices}
          currentDeviceId={currentRingDeviceId}
          onChange={handleRingDeviceChange}
          label={audioDeviceStrings.labels.ringOutput}
          disabled={disabled}
          loading={loading}
          errorMessage={
            // Device-switch errors take priority over speaker-test errors
            ringOutputSwitchError ?? ringSpeakerTestError
          }
          testButton={
            <AudioTestButton
              deviceId={currentRingDeviceId}
              isTesting={isTestingRingSpeaker}
              disabled={disabled || audioOutputDevices.length === 0}
              testService={ringTestService}
              setTesting={(v) => {
                audioDevicesState.isTestingRingSpeaker = v;
              }}
              setError={(v) => {
                audioDevicesState.ringSpeakerTestError = v;
              }}
              data-testid='audio-device-panel-test-ring-speaker'
            />
          }
        />
      )}
    </div>
  );
};
